import { api } from './http-client';
import { Listing, ListingDetail, CharterDetails } from '../interfaces/listing.interface';
import { PageDto } from '../types/pagination';

interface ListingsParams {
  status?: string;
  formType?: string;
  /** Matched against the listing title. */
  search?: string;
  page?: number;
  take?: number;
}

interface ManagedListingsParams extends ListingsParams {
  /** Matched against the seller's name, email or username. */
  postedBy?: string;
}

export type BulkListingAction = 'pending' | 'delete';

export interface BulkListingActionResult {
  updated: string[];
  blocked: { id: string; title: string | null; reason: string }[];
}

interface CompaniesParams {
  status?: string;
  page?: number;
  take?: number;
}

export interface CompanyAdminUser {
  id: string;
  email: string;
  fullName?: string;
  username?: string;
  registrationStatus?: string;
}

export interface Company {
  id: string;
  name: string;
  tradingName?: string;
  slug: string;
  logo?: string;
  coverImage?: string;
  phone?: string;
  email?: string;
  website?: string;
  location?: string;
  description?: string;
  bio?: string;
  primaryContactName?: string;
  brId?: string;
  brDocumentUrl?: string;
  brFileUrl?: string;
  isVerified: boolean;
  status: string;
  adminId: string;
  adminUser?: CompanyAdminUser;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface ListingCounts {
  pending: number;
  active: number;
  rejected: number;
}

export interface CvReviewItem {
  verificationId: string;
  listingId: string;
  cvUrl: string;
  status: string;
  submittedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
  listing: { id: string; title: string; formType: string; status: string } | null;
  seller: { id: string; name?: string; email?: string } | null;
}

export interface CvVerification {
  verificationId: string;
  listingId: string;
  cvUrl: string;
  status: string;
  submittedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

export const marketplaceService = {
  getListing: async (id: string): Promise<ListingDetail> => {
    const response = await api.request<ListingDetail>({
      url: `/admin/listings/${id}`,
      method: 'GET',
    });
    return response.data;
  },

  getCharterDetails: async (listingId: string): Promise<CharterDetails> => {
    const response = await api.request<CharterDetails>({
      url: `/admin/listings/${listingId}/charter-details`,
      method: 'GET',
    });
    return response.data;
  },

  getListings: async (params: ListingsParams): Promise<PageDto<Listing>> => {
    const response = await api.request<PageDto<Listing>>({
      url: '/admin/listings',
      method: 'GET',
      params,
    });
    return response.data;
  },

  getManagedListings: async (params: ManagedListingsParams): Promise<PageDto<Listing>> => {
    const response = await api.request<PageDto<Listing>>({
      url: '/admin/listings/manage',
      method: 'GET',
      params,
    });
    return response.data;
  },

  bulkListingAction: async (
    listingIds: string[],
    action: BulkListingAction,
  ): Promise<BulkListingActionResult> => {
    const response = await api.request<BulkListingActionResult>({
      url: '/admin/listings/bulk-action',
      method: 'POST',
      data: { listingIds, action },
    });
    return response.data;
  },

  approveListing: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/listings/${id}/approve`,
      method: 'POST',
    });
    return response.data;
  },

  rejectListing: async (id: string, reason: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/listings/${id}/reject`,
      method: 'POST',
      data: { reason },
    });
    return response.data;
  },

  getCompanies: async (params: CompaniesParams): Promise<PageDto<Company>> => {
    const response = await api.request<PageDto<Company>>({
      url: '/admin/companies',
      method: 'GET',
      params,
    });
    return response.data;
  },

  getCompany: async (id: string): Promise<Company> => {
    const response = await api.request<Company>({
      method: 'GET',
      url: `/admin/companies/${id}`,
    });
    return response.data;
  },

  approveCompany: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/companies/${id}/approve`,
      method: 'POST',
    });
    return response.data;
  },

  rejectCompany: async (id: string, reason: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/companies/${id}/reject`,
      method: 'POST',
      data: { reason },
    });
    return response.data;
  },

  getListingCounts: async (): Promise<ListingCounts> => {
    const response = await api.request<ListingCounts>({
      url: '/admin/listings/counts',
      method: 'GET',
    });
    return response.data;
  },

  getCvVerification: async (listingId: string): Promise<CvVerification | null> => {
    const response = await api.request<CvVerification | null>({
      url: `/admin/cv-review/${listingId}`,
      method: 'GET',
    });
    return response.data;
  },

  getCvReviewQueue: async (params: { status?: string; page?: number; take?: number }): Promise<PageDto<CvReviewItem>> => {
    const response = await api.request<PageDto<CvReviewItem>>({
      url: '/admin/cv-review',
      method: 'GET',
      params,
    });
    return response.data;
  },

  approveCv: async (listingId: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/cv-review/${listingId}/approve`,
      method: 'POST',
    });
    return response.data;
  },

  rejectCv: async (listingId: string, reason: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/cv-review/${listingId}/reject`,
      method: 'POST',
      data: { reason },
    });
    return response.data;
  },

  getCompanyDocumentPassword: async (companyId: string, masterKey: string): Promise<{ password: string | null }> => {
    const response = await api.request<{ password: string | null }>({
      url: `/admin/companies/${companyId}/document-password`,
      method: 'POST',
      data: { masterKey },
    });
    return response.data;
  },

  downloadCompanyBrDocument: async (companyId: string, filePassword: string): Promise<Blob> => {
    const response = await api.request<Blob>({
      url: `/admin/companies/${companyId}/br-document`,
      method: 'POST',
      data: { filePassword },
      responseType: 'blob',
    });
    return response.data;
  },

  getSuspendedBrokers: async (companyId: string): Promise<Array<{ id: string; fullName: string; email: string; username: string | null; avatar: string | null }>> => {
    const response = await api.request<Array<{ id: string; fullName: string; email: string; username: string | null; avatar: string | null }>>({
      url: `/admin/companies/${companyId}/suspended-brokers`,
      method: 'GET',
    });
    return response.data;
  },

  restoreCompany: async (companyId: string, newOwnerId: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/companies/${companyId}/restore`,
      method: 'POST',
      data: { newOwnerId },
    });
    return response.data;
  },
};
